import { memo } from 'react';
import { Stack, Center, Loader, Title, Text, ThemeIcon, TextInput } from '@mantine/core';
import { IconRocket, IconHash } from '@tabler/icons-react';
import { motion } from 'framer-motion';
import { COLORS } from '../../config/constants';

/**
 * LaunchStep component for Step 2 of the bot setup wizard.
 * Displays loading state or final confirmation before launching bots.
 *
 * @param {Object} props - Component properties.
 * @param {boolean} props.loading - Whether the launch process is in progress.
 * @param {Object} props.form - Current form state.
 * @param {Function} props.setForm - Function to update form state.
 * @param {Object} props.errors - Current form errors.
 * @param {Function} props.setErrors - Function to update form errors.
 * @param {Function} props.handleKeyDown - Key down event handler.
 * @param {Object} props.variants - Motion variants for animation.
 */
const LaunchStep = memo(({ loading, form, setForm, errors, setErrors, handleKeyDown, variants }) => {
    return (
        <motion.div
            key="step2"
            variants={variants}
            initial="hidden"
            animate="visible"
            exit="exit"
            style={{ width: '100%', position: 'absolute' }}
        >
            <Stack align="center" py="md">
                {loading ? (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                        <Center><Loader size={60} type="dots" color={COLORS.BLUE} /></Center>
                        <Title order={4} c="white" mt="md" ta="center">Initiating Sequence...</Title>
                        <Text c="dimmed" size="sm" ta="center">Verifying PIN and establishing connections.</Text>
                    </motion.div>
                ) : (
                    <>
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                        >
                            <ThemeIcon size={80} radius={100} variant="light" color={COLORS.BLUE}>
                                <IconRocket size={40} />
                            </ThemeIcon>
                        </motion.div>
                        <Title order={3} c="white">Ready for Takeoff</Title>
                        <Stack gap="xs" align="center" w="100%" maw={300}>
                            <Text c="dimmed" ta="center">
                                About to send <b>{form.amount}</b> players to pin:
                            </Text>
                            <TextInput
                                placeholder="123456"
                                leftSection={<IconHash size={16} />}
                                value={form.pin}
                                onChange={(e) => {
                                    setForm({ ...form, pin: e.target.value });
                                    if (setErrors) setErrors({ ...errors, pin: null });
                                }}
                                onKeyDown={(e) => handleKeyDown(e, 'launch')}
                                error={errors?.pin}
                                styles={{ 
                                    input: { textAlign: 'center', fontWeight: 700, fontSize: '1.1rem' }
                                }}
                                variant="filled"
                                radius="md"
                                w="100%"
                            />
                        </Stack>
                    </>
                )}
                <input
                    autoFocus={!loading}
                    style={{ opacity: 0, height: 0, position: 'absolute' }}
                    onKeyDown={(e) => handleKeyDown(e, 'launch')}
                />
            </Stack>
        </motion.div>
    );
});

export default LaunchStep;
